import { useReactiveVar } from '@apollo/client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import { FORM_INFO_SLOT_TYPES } from '../../../components/FormInfo/types';
import FormRadioCustomizations from '../../../components/FormRadio/Customizations/FormRadio';
import { ColorCircle, PatternCircle } from '../../../components/FormRadio/Customizations/styled';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES } from '../../../components/FormRadio/types';
import useUserProfile from '../../../hooks/useUserProfile';
import { vars } from '../../../reactive';
import { setFormPage } from '../../../reactive/actions';
import { decodeText, getKeys } from '../../../utils';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { isValidUrl } from '../../../utils/validators';

enum CustomizationRules {
  NONE = 'none',
  DURATION = 'duration',
  ASSIGN = 'assign',
  CREATE_VOUCHER = 'create_voucher',
  TAG_APPOINTMENTS = 'tag_appointments',
  OVERRIDE_ENABLE_CUSTOMIZATIONS_QUANTITY_FACTOR = 'override_enable_customizations_quantity_factor',
  BRANCH_SCHEDULES = 'branch_schedules'
}

const FormProductCustomizations = () => {
  const type = FORM_RADIO_TYPES.CUSTOMIZATIONS;
  const { registerAs } = FORM_RADIO_COMPONENT_TYPES[type];
  const formOptions = useForm();
  const currentBranch = vars.currentBranch();
  const isLoggedIn = useReactiveVar(vars.isLoggedIn);
  const selectedProduct = vars.selectedProduct();

  const customizationsMap: Record<
    string,
    {
      title: string;
      meta: Record<string, string>;
      price: number;
      rules?: {
        type: CustomizationRules;
        value: string;
      }[];
      customization_sort_index?: number;
      sort_index?: number;
      description?: string;
    }[]
  > = selectedProduct?.customizations || {};

  const titlesArray = getKeys(customizationsMap)
    .slice()
    .sort((a, b) => {
      const aSortIndex = customizationsMap[a][0]?.customization_sort_index;
      const bSortIndex = customizationsMap[b][0]?.customization_sort_index;
      if (aSortIndex !== undefined && bSortIndex !== undefined) {
        return aSortIndex - bSortIndex;
      }

      return a.localeCompare(b);
    });

  const customizations: {
    id: string;
    name: string;
    price: number;
    Indicator: false | '' | JSX.Element;
    title: string;
    meta: Record<string, string>;
    isPerNight: boolean;
  }[][] = titlesArray.map(property =>
    customizationsMap?.[property]
      ?.map(item => {
        const perNightRule = item.rules?.find(rule => rule.type === CustomizationRules.OVERRIDE_ENABLE_CUSTOMIZATIONS_QUANTITY_FACTOR);
        return {
          ...item,
          id: item?.title,
          name: item?.title,
          Indicator: item?.meta?.hex ? <ColorCircle color={item?.meta?.hex} /> : item?.meta?.url && isValidUrl.test(item?.meta?.url) && <PatternCircle src={`${item?.meta?.url}`} alt={item?.title} />,
          isPerNight: perNightRule ? perNightRule.value === 'true' : selectedProduct?.enable_customizations_quantity_factor || false
        };
      })
      ?.sort((a, b) => (a?.sort_index || 0) - (b?.sort_index || 0))
  );

  const [getProfile, { loading: userProfileLoading }] = useUserProfile(true);

  const onSubmit = (data: Record<string, any>) => {
    const selectedCustomizations: {
      fields: { [key: string]: { title: string; price: number } };
      quantity: number;
    } = {
      fields: Object.keys(data[registerAs]).reduce((all, key) => {
        const k = decodeText(key);
        const foundCustomization = customizationsMap?.[k]?.find(customization => data[registerAs][key] === customization.title);
        return {
          ...all,
          [k]: {
            title: foundCustomization?.title || '',
            price: Number(foundCustomization?.price || 0),
            description: foundCustomization?.description
          }
        };
      }, {}),
      quantity: 1
    };

    vars.selectedCustomizations([selectedCustomizations]);

    trackEvent(AnalyticsEvents.SERVICE_SELECTED, {
      name: selectedProduct?.name
    });

    const multiSlotProduct = selectedProduct?.booking_type === FORM_INFO_SLOT_TYPES.MULTI_SLOT;
    vars.recurrenceType('one_off');
    vars.selectedOrderGroupSlots([{ slots: [] }]);
    if (multiSlotProduct || selectedProduct?.type === 'subscription') {
      if (vars.isLoggedIn()) {
        getProfile();
      } else {
        setFormPage(FORM_PAGES.CREATE_ACCOUNT_EMAIL);
      }
      return;
    }

    if (selectedProduct?.allows_repeated_orders) {
      setFormPage(FORM_PAGES.BOOKING_CALENDAR_BOOKING_TYPE);
      return;
    }

    setFormPage(FORM_PAGES.BOOKING_CALENDAR_DATE_AND_TIME);
  };

  return (
    <FormRadioCustomizations
      type={type}
      formOptions={formOptions}
      titlesArray={titlesArray}
      customizations={customizations}
      onSubmit={onSubmit}
      borderColor={currentBranch?.colors?.secondary}
      loading={userProfileLoading}
      isLoggedIn={isLoggedIn}
    />
  );
};

export default FormProductCustomizations;
